/**
 * Renderer-local store for one Read Aloud request (`ReadAloudStore`). It sends
 * backend audio to an ordered playback queue (`ResponseAudioQueue`) and shares
 * the one speech-activity claim slot with Voice Mode and dictation, but plays
 * one text request, not a conversation.
 */
import { ResponseAudioQueue } from '@littlebird/sdk';
import { captureException } from '@sentry/react';
import { makeAutoObservable, observable } from 'mobx';
import { toast } from 'sonner';

import { claimSpeechActivity } from '@/features/speech-input/speech-activity-handoff';
import { littlebirdSdk } from '@/lib/sdk-init';
import { settingsStore } from '@/stores/settings.store';

import {
  BrowserAudioPlayback,
  logResponseAudioEvent,
} from './browser-audio-playback';
import {
  pickVoiceSampleText,
  VOICE_SAMPLE_READ_ALOUD_ID,
} from './response-audio-voices';
import { canUseSpeechFeatures } from './speech-features';

/** Network, playback, and speech-activity resources owned by one `play` call */
type ReadAloudRequest = {
  /** Request ID: `threadId:messageId` for messages, or the settings-preview ID */
  readAloudId: string;
  /** Chat whose deletion should stop playback; null for settings previews */
  ownerThreadId: string | null;
  /** Cancels the HTTP response stream */
  speechRequestAbortController: AbortController;
  /** Settles `play` normally after completion or an explicit stop */
  resolvePlaybackCompletion: () => void;
  /** Rejects `play` after synthesis, transport, or playback failure */
  rejectPlaybackCompletion: (error: Error) => void;
  /** Buffers and serially plays this request's response events */
  queue: ResponseAudioQueue;
  /** Releases this request's exclusive renderer speech-activity claim */
  releaseSpeechActivityClaim: () => void;
};

/**
 * Streams and plays speech for one text request at a time: either a finished
 * assistant message or a settings preview.
 *
 * The backend returns the speech in pieces, and playback begins after the first
 * piece instead of waiting for the whole message. Audio events and playback
 * callbacks act only while their request is still current, so stopping or
 * replacing a request prevents its old audio from playing.
 */
class ReadAloudStore {
  private currentRequest: ReadAloudRequest | null = null;

  /** ID of the active message or settings-preview request */
  get currentReadAloudId(): string | null {
    return this.currentRequest?.readAloudId ?? null;
  }

  /** Owning chat of the current request, or null outside a chat */
  get currentOwnerThreadId(): string | null {
    return this.currentRequest?.ownerThreadId ?? null;
  }

  constructor() {
    // Rerender observers only when the current request is replaced or cleared
    makeAutoObservable<ReadAloudStore, 'currentRequest'>(
      this,
      { currentRequest: observable.ref },
      { autoBind: true }
    );
  }

  /**
   * Stream and play one text, replacing any other renderer speech activity.
   * Resolve after playback finishes or is stopped; reject on failure.
   */
  async play(
    text: string,
    readAloudId: string,
    ownerThreadId: string | null = null
  ): Promise<void> {
    if (!text.trim()) throw new Error('Cannot read empty text aloud');
    // Recheck here because flags and settings can change while Read Aloud controls remain mounted
    // Deny before creating a paid text-to-speech request or any playback resources
    if (!canUseSpeechFeatures()) return;

    // Create the promise that keeps `play` pending until playback finishes, is stopped, or fails
    let resolvePlaybackCompletion = () => {};
    let rejectPlaybackCompletion = (_error: Error) => {};
    const playbackCompletion = new Promise<void>((resolve, reject) => {
      resolvePlaybackCompletion = resolve;
      rejectPlaybackCompletion = reject;
    });

    const request: ReadAloudRequest = {
      readAloudId,
      ownerThreadId,
      speechRequestAbortController: new AbortController(),
      resolvePlaybackCompletion,
      rejectPlaybackCompletion,
      queue: new ResponseAudioQueue({
        playback: new BrowserAudioPlayback(),
        // A final marker reporting failure still waits behind buffered audio:
        // the audio synthesized earlier finishes playing, then the request fails
        onResponseFinished: (_readAloudId, synthesisFailed) =>
          synthesisFailed
            ? this.failRequest(
                request,
                new Error('Read-aloud synthesis failed')
              )
            : this.finishRequest(request),
        onPlaybackError: (error) => this.failRequest(request, error),
        // Read Aloud plays exactly one reply: this request
        playbackOrder: () => [readAloudId],
        debug: logResponseAudioEvent,
      }),
      releaseSpeechActivityClaim: claimSpeechActivity(() =>
        this.stopCurrentRequest()
      ),
    };

    this.currentRequest = request;

    let sawFinalMarker = false;
    try {
      await littlebirdSdk.streamReadAloudAudioEvents(
        text,
        settingsStore.responseAudioVoiceId,
        (event) => {
          // Ignore events that arrive after this request was stopped or replaced
          if (this.currentRequest !== request) return;

          // Use `readAloudId` for every event so the queue treats this request as one reply
          request.queue.enqueue({ ...event, userMessageId: readAloudId });
          if (event.kind === 'final') {
            sawFinalMarker = true;
            // Stop intake once the final marker has entered the playback queue
            request.speechRequestAbortController.abort();
          }
        },
        request.speechRequestAbortController.signal
      );
      // A stream that ends without its final marker is a failure; this
      // throw rejects the `play` promise via `failRequest`
      if (this.currentRequest === request && !sawFinalMarker) {
        throw new Error('Read-aloud stream ended without a final marker');
      }
    } catch (error) {
      // The final marker aborts intake; that rejection must not fail queued playback
      if (!sawFinalMarker) this.failRequest(request, error);
    }

    await playbackCompletion;
  }

  /** Stop network and playback work, settling the active `play` call normally */
  stopCurrentRequest(): void {
    const request = this.currentRequest;
    this.currentRequest = null;
    request?.releaseSpeechActivityClaim();
    request?.speechRequestAbortController.abort();
    request?.queue.stop();
    request?.resolvePlaybackCompletion();
  }

  /**
   * Reject the current `play` call and release its network, queue, and speech
   * resources. A late failure from a replaced request is ignored.
   */
  private failRequest(request: ReadAloudRequest, error: unknown): void {
    if (this.currentRequest !== request) return;
    this.currentRequest = null;
    request.releaseSpeechActivityClaim();
    request.speechRequestAbortController.abort();
    request.queue.stop();
    request.rejectPlaybackCompletion(
      error instanceof Error ? error : new Error(String(error))
    );
  }

  /**
   * Resolve the current `play` call after its final marker reaches the front of
   * the queue. Earlier audio has finished, so no queue shutdown is needed.
   */
  private finishRequest(request: ReadAloudRequest): void {
    if (this.currentRequest !== request) return;
    this.currentRequest = null;
    request.releaseSpeechActivityClaim();
    request.resolvePlaybackCompletion();
  }
}

/** Renderer-local owner of the one active Read Aloud request */
export const readAloudStore = new ReadAloudStore();

/** Save a voice choice and play its settings-page preview */
export async function selectAndSampleVoice(voiceId: string): Promise<void> {
  // Keep the selected voice even if its preview fails
  settingsStore.setResponseAudioVoiceId(voiceId);
  try {
    await readAloudStore.play(
      pickVoiceSampleText(),
      VOICE_SAMPLE_READ_ALOUD_ID
    );
  } catch (error) {
    captureException(error);
    // Preview failures are otherwise invisible because the selected voice remains saved
    toast.error("Couldn't play that voice");
  }
}

/** Stop only the settings preview, leaving message playback untouched */
export function stopVoiceSample(): void {
  if (readAloudStore.currentReadAloudId !== VOICE_SAMPLE_READ_ALOUD_ID) return;
  readAloudStore.stopCurrentRequest();
}
